import { type ComponentPropsWithoutRef } from 'react';
import { Table as MantineTable } from '@mantine/core';
import { Items, Skeletons } from './components';
import { type TableData, type TableRow } from './types';

type MantineTableProps = ComponentPropsWithoutRef<typeof MantineTable>;

type TableProps<Item> = {
    data: TableData<Item>;
    onRowClick?: (item: Item) => void;
    loading?: boolean;
    selectedRowKey?: TableRow<Item>['key'];
} & Omit<MantineTableProps, 'data'>;

export const Table = <Item,>({
    data,
    onRowClick,
    loading = false,
    striped = true,
    highlightOnHover = true,
    stickyHeader = true,
    selectedRowKey,
    ...otherProps
}: TableProps<Item>) => {
    const { caption, head, rows = [], foot } = data;

    return (
        <MantineTable
            striped={striped}
            highlightOnHover={highlightOnHover}
            stickyHeader={stickyHeader}
            {...otherProps}
        >
            {caption && <MantineTable.Caption>{caption}</MantineTable.Caption>}

            {head.length > 0 && (
                <MantineTable.Thead>
                    <MantineTable.Tr>
                        {head.map(({ key, content, ta }) => (
                            <MantineTable.Th
                                key={key}
                                ta={ta}
                            >
                                {content}
                            </MantineTable.Th>
                        ))}
                    </MantineTable.Tr>
                </MantineTable.Thead>
            )}

            <MantineTable.Tbody>
                {loading ? (
                    <Skeletons head={head} />
                ) : (
                    <Items
                        rows={rows}
                        onClick={onRowClick}
                        selectedRowKey={selectedRowKey}
                    />
                )}
            </MantineTable.Tbody>

            {foot && foot.length > 0 && (
                <MantineTable.Tfoot>
                    <MantineTable.Tr>
                        {foot.map(({ key, content }) => (
                            <MantineTable.Th key={key}>{content}</MantineTable.Th>
                        ))}
                    </MantineTable.Tr>
                </MantineTable.Tfoot>
            )}
        </MantineTable>
    );
};
